import { type NavItem, SidebarNav } from "@app/ui/next"
import { useLocation, useNavigate } from "@tanstack/react-router"
import type { MouseEventHandler, ReactNode } from "react"

/**
 * Sidebar navigation for the authenticated Helm shell.
 *
 * Renders the RAM-OS `SidebarNav` (section groups + active accent bar) with
 * each entry as an anchor. `@app/ui/next` is router-agnostic, so we supply
 * the `href` (= route path) and a client-nav `onClick`; modified clicks
 * (cmd/ctrl/shift/middle) fall through to the browser so open-in-new-tab and
 * new-window keep working. `activeId` is derived from `useLocation` — exact
 * match for the dashboard root, prefix match for every other section.
 */

// Compact inline glyphs (stroke-based, currentColor) so the rail reads as an
// app nav without pulling in an icon dependency.
const icon = (path: ReactNode): ReactNode => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		{path}
	</svg>
)

// Route paths the nav links to (kept as a literal union so TanStack's typed
// `navigate({ to })` accepts them without widening to a bare string).
type NavTo = "/" | "/shipments" | "/lots" | "/parties" | "/notifications" | "/settings"

interface NavConfigItem {
	readonly id: string
	readonly label: string
	readonly to: NavTo
	readonly icon: ReactNode
}

interface NavSection {
	readonly label: string
	readonly items: readonly NavConfigItem[]
}

const NAV_SECTIONS: readonly NavSection[] = [
	{
		label: "Operations",
		items: [
			{
				id: "dashboard",
				label: "Dashboard",
				to: "/",
				icon: icon(
					<>
						<rect x="3" y="3" width="7" height="9" />
						<rect x="14" y="3" width="7" height="5" />
						<rect x="14" y="12" width="7" height="9" />
						<rect x="3" y="16" width="7" height="5" />
					</>,
				),
			},
			{
				id: "shipments",
				label: "Shipments",
				to: "/shipments",
				icon: icon(
					<>
						<path d="M3 7h13v10H3z" />
						<path d="M16 10h4l1 3v4h-5z" />
						<circle cx="7" cy="18" r="1.6" />
						<circle cx="17.5" cy="18" r="1.6" />
					</>,
				),
			},
			{
				id: "lots",
				label: "Lots",
				to: "/lots",
				icon: icon(
					<>
						<path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" />
						<path d="M12 12l8-4.5M12 12v9M12 12L4 7.5" />
					</>,
				),
			},
			{
				id: "parties",
				label: "Parties",
				to: "/parties",
				icon: icon(
					<>
						<circle cx="9" cy="8" r="3" />
						<path d="M3 20a6 6 0 0 1 12 0" />
						<path d="M16 5a3 3 0 0 1 0 6M16 14a6 6 0 0 1 5 6" />
					</>,
				),
			},
		],
	},
	{
		label: "Admin",
		items: [
			{
				id: "notifications",
				label: "Notifications",
				to: "/notifications",
				icon: icon(
					<>
						<path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
						<path d="M10 20a2 2 0 0 0 4 0" />
					</>,
				),
			},
			{
				id: "settings",
				label: "Settings",
				to: "/settings",
				icon: icon(
					<>
						<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
						<circle cx="12" cy="12" r="3" />
					</>,
				),
			},
		],
	},
]

const isActive = (to: NavTo, pathname: string): boolean =>
	to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`)

export function AppNav() {
	const { pathname } = useLocation()
	const navigate = useNavigate()

	// Client-side navigation for a plain left-click; modified/middle clicks fall
	// through to the browser (new tab / window) since the item is a real anchor.
	const onNavClick =
		(to: NavTo): MouseEventHandler<HTMLAnchorElement> =>
		(e) => {
			if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
			e.preventDefault()
			void navigate({ to })
		}

	const items: NavItem[] = []
	for (const section of NAV_SECTIONS) {
		items.push({ section: section.label })
		for (const it of section.items) {
			items.push({
				id: it.id,
				label: it.label,
				icon: it.icon,
				href: it.to,
				onClick: onNavClick(it.to),
			})
		}
	}

	const activeId = NAV_SECTIONS.flatMap((s) => s.items).find((it) => isActive(it.to, pathname))?.id

	return <SidebarNav items={items} aria-label="Primary" {...(activeId ? { activeId } : {})} />
}
